/**
 * Config and utils for filters that can be applied to a an item-type
 *
 */

import isEqual from 'lodash/isEqual';
import {DateTime} from 'luxon';

export const KEYS = {
  CLOUD_COVER: 'cloud_cover',
  GSD: 'gsd',
  OFF_NADIR_ANGLE: 'view_angle',
  SUN_AZIMUTH: 'sun_azimuth',
  SUN_ELEVATION: 'sun_elevation',
  GROUND_CONTROL: 'ground_control',
  QUALITY_CATEGORY: 'quality_category',
};

export const PS_INSTRUMENTS = {PS2: false, 'PS2.SD': false, 'PSB.SD': false};
export const INSTRUMENT_LABELS = {
  PS2: 'Dove Classic',
  'PS2.SD': 'Dove R',
  'PSB.SD': 'Super Dove',
};
export const formatInstrumentLabel = instrument =>
  INSTRUMENT_LABELS[instrument]
    ? `${INSTRUMENT_LABELS[instrument]} (${instrument})`
    : instrument;

export const PUBLISHING_STAGES_DEFAULT = {
  preview: false,
  standard: true,
  finalized: true,
};

// TODO: Get a canonical list of field filter types
// https://www.planet.com/docs/reference/data-api/search-api/

export const DEFAULT_VALUES = {
  [KEYS.CLOUD_COVER]: [0, 1],
  [KEYS.GSD]: [0.1, 30],
  [KEYS.OFF_NADIR_ANGLE]: [-60, 60],
  [KEYS.SUN_AZIMUTH]: [0, 360],
  [KEYS.SUN_ELEVATION]: [0, 90],
  [KEYS.GROUND_CONTROL]: null,
  [KEYS.QUALITY_CATEGORY]: null,
};

// TODO: localize names and descriptions
export const CONFIGS = Object.freeze({
  [KEYS.CLOUD_COVER]: {
    id: KEYS.CLOUD_COVER,
    field_name: KEYS.CLOUD_COVER,
    type: 'RangeFilter',
    component: 'slider',
    range: [0, 1],
    defaults: DEFAULT_VALUES[KEYS.CLOUD_COVER],
    unit: 'common:unit.percent',
    scale: x => Math.round(x * 100),
    resetScale: x => x / 100,
    formatValues: function (values) {
      return values.map(value => Math.round(value * 100));
    },
    name: 'filters.cloudCover',
    description: 'filters.cloudCoverDescription',
  },
  [KEYS.GSD]: {
    id: KEYS.GSD,
    field_name: KEYS.GSD,
    type: 'RangeFilter',
    component: 'slider',
    range: [0.1, 30],
    defaults: DEFAULT_VALUES[KEYS.GSD],
    unit: 'common:unit.meters',
    formatter: x => Math.round(x * 100) / 100,
    formatValues: function (values) {
      const range = values.map(value => Math.round(value * 100) / 100);
      return range;
    },
    name: 'filters.groundSampleDistance',
    description: 'filters.groundSampleDistanceDescription',
  },
  [KEYS.OFF_NADIR_ANGLE]: {
    id: KEYS.OFF_NADIR_ANGLE,
    field_name: KEYS.OFF_NADIR_ANGLE,
    type: 'RangeFilter',
    component: 'slider',
    range: [-60, 60],
    defaults: DEFAULT_VALUES[KEYS.OFF_NADIR_ANGLE],
    unit: 'common:unit.degrees',
    formatter: x => Math.round(x),
    formatValues: function (values) {
      return values.map(Math.round);
    },
    name: 'filters.offNadirAngle',
    description: 'filters.offNadirAngleDescription',
  },
  [KEYS.SUN_AZIMUTH]: {
    id: KEYS.SUN_AZIMUTH,
    field_name: KEYS.SUN_AZIMUTH,
    type: 'RangeFilter',
    component: 'slider',
    range: [0, 360],
    defaults: DEFAULT_VALUES[KEYS.SUN_AZIMUTH],
    unit: 'common:unit.degrees',
    formatter: x => Math.round(x),
    formatValues: function (values) {
      return values.map(Math.round);
    },
    name: 'filters.sunAzimuth',
    description: 'filters.sunAzimuthDescription',
  },
  [KEYS.SUN_ELEVATION]: {
    id: KEYS.SUN_ELEVATION,
    field_name: KEYS.SUN_ELEVATION,
    type: 'RangeFilter',
    component: 'slider',
    range: [-90, 90],
    defaults: DEFAULT_VALUES[KEYS.SUN_ELEVATION],
    unit: 'common:unit.degrees',
    formatter: x => Math.round(x),
    formatValues: function (values) {
      return values.map(Math.round);
    },
    name: 'filters.sunElevation',
    description: 'filters.sunElevationDescription',
  },
  [KEYS.GROUND_CONTROL]: {
    id: KEYS.GROUND_CONTROL,
    type: 'StringInFilter',
    component: 'toggle',
    field_name: KEYS.GROUND_CONTROL,
    defaults: DEFAULT_VALUES[KEYS.GROUND_CONTROL],
    name: 'filters.groundControl',
    description: 'filters.groundControlDescription',
    checked: 'true',
  },
  [KEYS.QUALITY_CATEGORY]: {
    id: KEYS.QUALITY_CATEGORY,
    type: 'StringInFilter',
    component: 'toggle',
    field_name: KEYS.QUALITY_CATEGORY,
    defaults: DEFAULT_VALUES[KEYS.QUALITY_CATEGORY],
    name: 'filters.qualityCategory',
    description: 'filters.qualityCategoryDescription',
    checked: 'standard',
    learnMore:
      'https://docs.planet.com/data/imagery/planetscope/#standard-versus-test-imagery',
  },
});

export const EXCLUDED_FILTERS = {};

/** Utility functions */

export const hasChangedFromDefaults = filter => {
  const {values, defaults} = filter;

  if (values) {
    if (defaults) {
      return !isEqual(values, defaults);
    }
    return true;
  }
  return false;
};

export const toAPIFilter = filter => {
  const {type, values, field_name} = filter;

  let config;
  if (type === 'StringInFilter') {
    config = values;
  } else if (type === 'RangeFilter') {
    config = {
      gte: values[0],
      lte: values[1],
    };
  }

  // Hack/clever filter to fallback to cloud cover. https://hello.planet.com/jira/browse/CSS-1773
  if (field_name === KEYS.CLOUD_COVER) {
    return {
      type: 'OrFilter',
      config: [
        {
          type: 'RangeFilter',
          field_name: 'cloud_percent',
          config: {
            gte: values[0] * 100,
            lte: values[1] * 100,
          },
        },
        {
          type: 'AndFilter',
          config: [
            {
              type: 'NotFilter',
              config: {
                type: 'RangeFilter',
                field_name: 'cloud_percent',
                config: {
                  gte: 0,
                  lte: 100,
                },
              },
            },
            {
              type: 'RangeFilter',
              field_name: 'cloud_cover',
              config: {
                gte: values[0],
                lte: values[1],
              },
            },
          ],
        },
      ],
    };
  }

  const filterObj = {
    type,
    config,
  };
  if (field_name) {
    filterObj['field_name'] = field_name;
  }
  return filterObj;
};

/**
 * exploit the naturally recursive nature of JSON.stringify to find the objects with
 * the specified key - value combination
 * This is helpful for loading a saved search and converting to our objects
 * https://stackoverflow.com/a/56204398
 * @param {*} entireObj
 * @param {*} keyToFind
 * @param {*} valToFind
 * @returns Array
 */
export const findNestedObj = (entireObj, keyToFind, valToFind) => {
  const foundObj = [];
  JSON.stringify(entireObj, (_, nestedValue) => {
    if (nestedValue && nestedValue[keyToFind] === valToFind) {
      foundObj.push(nestedValue);
    }
    return nestedValue;
  });
  return foundObj;
};

export const getGeometry = filter => {
  if (filter.type === 'GeometryFilter') {
    return filter.config;
  }
  if (Array.isArray(filter.config)) {
    for (let i = 0; i < filter.config.length; i++) {
      const result = getGeometry(filter.config[i]);
      if (result) {
        return result;
      }
    }
  }
};

export const dateRangesToISO = dateRanges =>
  dateRanges.map(range => {
    const start = range[0]
      ? DateTime.fromISO(range[0], {zone: 'utc'})
      : DateTime.utc();
    const end = range[1]
      ? DateTime.fromISO(range[1], {zone: 'utc'})
      : DateTime.utc();
    return [start.startOf('day').toISO(), end.endOf('day').toISO()];
  });
